import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

function WFHLimitSetup() {
  const [limits, setLimits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ department_id: '', days_per_month: 4, period_type: 'monthly' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, d] = await Promise.allSettled([hrms.getWFHLimits(), hrms.getDepartments()]);
      if (l.status === 'fulfilled') setLimits(l.value.data || []);
      if (d.status === 'fulfilled') { const dd = d.value.data; setDepartments(Array.isArray(dd) ? dd : dd.departments || []); }
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ department_id: '', days_per_month: 4 });
    setModal(true);
  };

  const openEdit = (lim) => {
    setEditing(lim);
    setForm({ department_id: lim.department_id ?? '', days_per_month: lim.days_per_month, period_type: lim.period_type || 'monthly' });
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.days_per_month || form.days_per_month < 0) { alert('Enter valid days per month'); return; }
    setSaving(true);
    try {
      const payload = {
        department_id: form.department_id === '' ? null : Number(form.department_id),
        days_per_month: Number(form.days_per_month),
        period_type: form.period_type,
      };
      if (editing) {
        await hrms.updateWFHLimit(editing.id, payload);
      } else {
        await hrms.createWFHLimit(payload);
      }
      setModal(false);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this WFH limit?')) return;
    try { await hrms.deleteWFHLimit(id); load(); } catch { alert('Failed to delete'); }
  };

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>WFH Limit Setup</h1>
        <Button variant="primary" onClick={openAdd}>+ Add Limit</Button>
      </div>

      <Card padding="0">
        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>Loading...</p>
        ) : limits.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: theme.textMuted }}>No WFH limits configured. The default is 4 days/month.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Department', 'Limit', 'Period', 'Actions'].map((h) => (
                  <th key={h} style={{ padding: '0.75rem 1.25rem', textAlign: 'left', fontWeight: 600, color: theme.textMuted, borderBottom: `2px solid ${theme.border}`, fontSize: '0.78rem', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {limits.map((lim) => (
                <tr key={lim.id} style={{ borderBottom: `1px solid ${theme.border}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#F8FAFC')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ padding: '0.75rem 1.25rem' }}>{lim.department_name || 'All Departments'}</td>
                  <td style={{ padding: '0.75rem 1.25rem' }}>
                    <span style={{ background: `${theme.primary}18`, color: theme.primary, padding: '0.2rem 0.6rem', borderRadius: '1rem', fontWeight: 700 }}>{lim.days_per_month} days</span>
                  </td>
                  <td style={{ padding: '0.75rem 1.25rem', textTransform: 'capitalize', color: theme.textMuted, fontSize: '0.8rem' }}>
                    {lim.period_type || 'Monthly'}
                  </td>
                  <td style={{ padding: '0.75rem 1.25rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button size="sm" variant="outline" onClick={() => openEdit(lim)}>Edit</Button>
                      <Button size="sm" variant="danger" onClick={() => handleDelete(lim.id)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? 'Edit WFH Limit' : 'Add WFH Limit'}
        footer={<><Button variant="outlineGray" onClick={() => setModal(false)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleSave}>Save</Button></>}>
        <label style={lbl}>Department</label>
        <select style={inp} value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
          <option value="">All Departments (Global)</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <label style={lbl}>Period *</label>
        <select style={inp} value={form.period_type} onChange={(e) => setForm({ ...form, period_type: e.target.value })}>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </select>
        <label style={lbl}>Days Allowed Per {form.period_type === 'monthly' ? 'Month' : form.period_type === 'quarterly' ? 'Quarter' : 'Year'} *</label>
        <input style={inp} type="number" min="0" max="366" value={form.days_per_month} onChange={(e) => setForm({ ...form, days_per_month: e.target.value })} />
      </Modal>
    </div>
  );
}

export default WFHLimitSetup;
